import React from "react";

import AddIcon from "@mui/icons-material/Add";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { Box, Button, Stack, Typography } from "@mui/material";
import { IBookmark, UserLight } from "store/types";

import { PageGroup } from "./PageGroup";
import { PanelEmptyAll, PanelError, PanelLoading } from "./PanelStates";
import { SharedRow } from "./SharedRow";
import { CurrentPageContext, PanelStatus } from "./types";

export type OwnedGroup = { label: string; key: string; items: IBookmark[] };

interface Props {
  status: PanelStatus;
  ownedGroups: OwnedGroup[];
  sharedWithMe: IBookmark[];
  ownersById: Map<number, UserLight>;
  currentPage: CurrentPageContext | null;
  canSave: boolean;
  onClose: () => void;
  onRetry: () => void;
  onSaveClick: () => void;
}

export const BookmarkPanel: React.FC<Props> = ({
  status,
  ownedGroups,
  sharedWithMe,
  ownersById,
  currentPage,
  canSave,
  onClose,
  onRetry,
  onSaveClick
}) => {
  const hasAnyData = Boolean(ownedGroups.length) || Boolean(sharedWithMe.length);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        sx={{ padding: "20px 24px 8px 24px" }}
      >
        <Stack direction="row" alignItems="center" gap="6px">
          <BookmarkBorderIcon sx={{ fontSize: "20px", color: "#071f35" }} />
          <Typography sx={{ fontSize: "18px", fontWeight: 400, color: "#071f35", lineHeight: "24px" }}>
            Saved views
          </Typography>
        </Stack>
        {currentPage && canSave && (
          <Button
            onClick={onSaveClick}
            variant="contained"
            sx={{
              height: "24px",
              minHeight: 0,
              padding: "4px 8px",
              borderRadius: "32px",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
              textTransform: "none",
              whiteSpace: "nowrap",
              boxShadow: "none",
              backgroundColor: "#071f35",
              "&:hover": { backgroundColor: "#0d2c4a", boxShadow: "none" }
            }}
            startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
          >
            Save view of {currentPage.pageLabel}
          </Button>
        )}
      </Stack>

      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {status === "loading" && <PanelLoading />}
        {status === "error" && <PanelError onRetry={onRetry} />}
        {status === "ready" && !hasAnyData && <PanelEmptyAll />}
        {status === "ready" && hasAnyData && (
          <Stack gap="20px" sx={{ padding: "12px 0 24px 0" }}>
            {ownedGroups.map((group, index) => (
              <React.Fragment key={group.key}>
                {index > 0 && <ListDivider />}
                <PageGroup
                  label={group.label}
                  isCurrent={currentPage?.pageKey === group.key}
                  bookmarks={group.items}
                  onApplied={onClose}
                />
              </React.Fragment>
            ))}

            {Boolean(ownedGroups.length) && <ListDivider />}

            <Stack>
              <Typography
                sx={{
                  padding: "4px 24px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#496682",
                  lineHeight: "16px",
                  textTransform: "uppercase"
                }}
              >
                Shared with me
              </Typography>
              {!sharedWithMe.length ? (
                <Typography sx={{ padding: "4px 24px", fontSize: "14px", color: "#496682" }}>
                  No views shared with you.
                </Typography>
              ) : (
                sharedWithMe.map((bookmark) => (
                  <SharedRow
                    key={bookmark.id}
                    bookmark={bookmark}
                    owner={ownersById.get(bookmark.user_id)}
                    onApplied={onClose}
                  />
                ))
              )}
            </Stack>
          </Stack>
        )}
      </Box>
    </>
  );
};

const ListDivider: React.FC = () => (
  <Box sx={{ height: "1px", backgroundColor: "#dbe8f5", marginX: "24px" }} />
);
